import React, { FunctionComponent } from 'react';
import styles from './TransportationHeaderSummary.Styles';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { TooltipTextField } from '../../../../../generic-components/tooltip-textfield/TooltipTextField.Component';
import ServiceTypeRequestTransportationEntity from '../../../../../../entities/ServiceTypeRequestTransportation.Entity';
import OriginationEntity from '../../../../../../entities/Origination.Entity';
import AppointmentTypeEntity from '../../../../../../entities/AppointmentType.Entity';
import DestinationEntity from '../../../../../../entities/Destination.Entity';
import TransportationRequiredEntity from '../../../../../../entities/TransportationRequired.Entity';
import Moment from 'moment';
import { WheelchairOrStrecherDetailEntity } from '../../../../../../entities/WheelchairOrStrecherDetail.Entity';
import { MaterialTableProps } from 'material-table';
import MaterialTable from '../../../../../generic-components/material-table/MaterialTable.Component';
import DestinationInformationEntity from '../../../../../../entities/DestinationInformation.Entity';
import StateEntity from '../../../../../../entities/State.Entity';
import { TT_AUTHORIZATION_NOTES_LABEL } from '../../../../referral-add/referral-service/TtServiceConstants';

const SHOW_LEGACY_TRANSPORTATION_REQUEST_FIELDS = false;

interface ITransportationHeaderSummaryProps {
    serviceTypeRequestTransportation: ServiceTypeRequestTransportationEntity;
    appointmentTypes: Array<AppointmentTypeEntity>;
    transportationRequireds: Array<TransportationRequiredEntity>;
    originations: Array<OriginationEntity>;
    destinations: Array<DestinationEntity>;
    wheelchairOrStretcherDetails: Array<WheelchairOrStrecherDetailEntity>;
    states: Array<StateEntity>;
}

function formatClaimantHeight(heightFt?: number, heightInches?: number): string {
    if (!heightFt) {
        return '-';
    }

    const inchesSuffix = heightInches ? `${heightInches} inches` : '';
    return `${heightFt} feet ${inchesSuffix}`.trim();
}

function formatDestinationSummary(
    destinationRows: DestinationInformationEntity[] | undefined,
    destinationCatalog: DestinationEntity[],
): string {
    const rows = destinationRows ?? [];
    if (rows.length === 0) {
        return '-';
    }

    return rows
        .map((row) => {
            if (row.destinationId === 0) {
                return 'Other';
            }
            return destinationCatalog.find((d) => d.destinationId === row.destinationId)?.destinationName ?? '-';
        })
        .join(', ');
}

export const TransportationHeaderSummary: FunctionComponent<ITransportationHeaderSummaryProps> = (
    props: ITransportationHeaderSummaryProps,
) => {
    const classes = styles();
    const transportation = props.serviceTypeRequestTransportation;

    const destinationsTableProps: MaterialTableProps<DestinationInformationEntity> = {
        columns: [
            {
                title: 'Destination',
                render: (rowData) => {
                    if (rowData.destinationId == 0) {
                        return 'Other';
                    }
                    return props.destinations.find((d) => d.destinationId === rowData.destinationId)?.destinationName;
                },
            },
            {
                title: 'Destination Address',
                render: (rowData) => {
                    const stateName = rowData?.destinationStateId
                        ? props.states?.find((x) => x.stateId === rowData.destinationStateId)?.stateName ?? ''
                        : '';
                    return `${rowData?.destinationAddress || ''} ${rowData?.destinationCity || ''} ${stateName} ${rowData?.destinationZipCode || ''}`;
                },
            },
            {
                title: 'Destination Contact Number',
                field: 'destinationContactNumber',
            },
            {
                title: 'Appointment Time',
                render: (rowData) => {
                    return rowData?.appointmentTime?.length > 5
                        ? rowData?.appointmentTime?.split('T')[1].substring(0, 5)
                        : rowData?.appointmentTime;
                },
            },
            {
                title: 'Appointment Length (Min)',
                field: 'appointmentLength',
            },
            {
                title: 'Round-trip',
                render: (rowData) => {
                    return rowData?.roundTrip ? 'Yes' : 'No';
                },
            },
        ],
        data: transportation.destinations ?? [],
        options: {
            selection: false,
            paging: false,
            toolbar: false,
            actionsColumnIndex: -1,
        },
    };

    return (
        <Accordion defaultExpanded={true} className={classes.paper}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                aria-controls="panel1a-content"
                id="cliam-accordion"
                className={classes.paperHeader}
            >
                <Typography variant="subtitle2" className={classes.sidebarTitle}>
                    Transportation Service
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={3} className={classes.gridContainer}>
                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Transportation Required"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={
                                transportation?.transportationRequiredId === 0
                                    ? 'Other'
                                    : props.transportationRequireds?.find(
                                          (x) =>
                                              x.transportationRequiredId === transportation?.transportationRequiredId,
                                      )?.transportationRequiredName ?? 'Other'
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Other Transportation Required"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={
                                transportation?.transportationRequiredId === 0
                                    ? transportation?.otherTransportationRequired
                                    : '-'
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Appointment Date"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={Moment(transportation?.appointmentDate).format('MM/DD/YYYY')}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Frequency and Duration"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={transportation?.frequencyAndDuration ? transportation.frequencyAndDuration : '-'}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Origination"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={
                                transportation?.originationId === 0
                                    ? 'Other'
                                    : props.originations?.find(
                                          (x) => x.originationId === transportation?.originationId,
                                      )?.originationName ?? 'Other'
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TooltipTextField
                            type="typography"
                            label="Origination Contact Number"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={transportation?.originationContactNumber ? transportation.originationContactNumber : '-'}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <Typography variant="body2" className={classes.dataLabel}>
                            Rush Request
                        </Typography>
                        <Typography variant="subtitle2" className={classes.blackFont}>
                            {transportation?.rushRequest ? 'Yes' : 'No'}
                        </Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <TooltipTextField
                            type="typography"
                            label="Destination"
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={formatDestinationSummary(transportation?.destinations, props.destinations)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TooltipTextField
                            type="typography"
                            label={TT_AUTHORIZATION_NOTES_LABEL}
                            typographyLabelClass={classes.dataLabel}
                            typographyValueClass={classes.blackFont}
                            value={transportation?.specialInstructions ? transportation.specialInstructions : '-'}
                        />
                    </Grid>

                    {SHOW_LEGACY_TRANSPORTATION_REQUEST_FIELDS && (
                        <>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Wheelchair / Stretcher details"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={
                                        transportation?.wheelchairOrStretcherDetails === 0
                                            ? 'Other'
                                            : props.wheelchairOrStretcherDetails?.find(
                                                  (x) =>
                                                      x.wheelchairOrStretcherDetailId ===
                                                      transportation?.wheelchairOrStretcherDetails,
                                              )?.wheelchairOrStretcherDetailName ?? 'Other'
                                    }
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Facility Name"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={
                                        transportation?.originationFacilityName
                                            ? transportation.originationFacilityName
                                            : '-'
                                    }
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Origination Address"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={transportation?.originationAddress ? transportation.originationAddress : '-'}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="City"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={transportation?.originationCity ? transportation.originationCity : '-'}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant="body2" className={classes.dataLabel}>
                                    State
                                </Typography>
                                <Typography variant="subtitle2" className={classes.blackFont}>
                                    {props.states?.find((x) => x.stateId === transportation?.originationStateId)
                                        ?.stateName ?? ''}
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Zip Code"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={transportation?.originationZipCode ? transportation.originationZipCode : '-'}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <MaterialTable tableProps={destinationsTableProps} />
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Additional Passengers"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={
                                        transportation?.additionalPassengers ? transportation.additionalPassengers : '-'
                                    }
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Number of Steps"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={transportation?.numberOfSteps ? transportation.numberOfSteps : '-'}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Rental"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={transportation?.rental ? 'Yes' : 'No'}
                                />
                            </Grid>
                            <Grid item container spacing={0} xs={3}>
                                <Grid item xs={6}>
                                    <TooltipTextField
                                        type="typography"
                                        label="Oxygen"
                                        typographyLabelClass={classes.dataLabel}
                                        typographyValueClass={classes.blackFont}
                                        value={transportation?.oxygen ? 'Yes' : 'No'}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TooltipTextField
                                        type="typography"
                                        label="Oxygen Level"
                                        typographyLabelClass={classes.dataLabel}
                                        typographyValueClass={classes.blackFont}
                                        value={transportation?.oxygenLevel ?? '-'}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Medical Monitoring"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={transportation?.medicalMonitoring ? 'Yes' : 'No'}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Typography>Claimant information:</Typography>
                            </Grid>
                            <Grid item container xs={3} spacing={0}>
                                <Grid item xs={6}>
                                    <TooltipTextField
                                        type="typography"
                                        label="Height"
                                        typographyLabelClass={classes.dataLabel}
                                        typographyValueClass={classes.blackFont}
                                        value={formatClaimantHeight(
                                            transportation?.heightFt,
                                            transportation?.heightInches,
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={3}>
                                <TooltipTextField
                                    type="typography"
                                    label="Weight"
                                    typographyLabelClass={classes.dataLabel}
                                    typographyValueClass={classes.blackFont}
                                    value={
                                        transportation?.weightLbs ? `${transportation.weightLbs} lbs` : '-'
                                    }
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};
